import { useState, useEffect, useCallback } from "react";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import type { UnderwritingDefaults } from "@shared/schema";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { SetupCompletenessBanner } from "@/components/setup-completeness-banner";
import { AccountSetupPending, useStarterSeedPending } from "@/components/account-setup-pending";
import { UNDERWRITING_REQUIRED_FIELDS, getMissingUnderwritingFields } from "@shared/buy-box-setup";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { usePageTitle } from "@/hooks/use-page-title";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function Field({ label, value, onChange, onBlur, type = "text", suffix, testId }: {
  label: string; value: string; onChange: (val: string) => void; onBlur: () => void; type?: string; suffix?: string; testId: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <div className="relative">
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          className="text-sm"
          data-testid={testId}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">{suffix}</span>}
      </div>
    </div>
  );
}

export default function AppUnderwritingDefaults() {
  const queryClient = useQueryClient();
  usePageTitle("Underwriting Defaults");
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("underwriting");
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [localFields, setLocalFields] = useState<Record<string, unknown>>({});

  const seedingPending = useStarterSeedPending();

  const defaultsQuery = useQuery<UnderwritingDefaults>({
    queryKey: ["/api/app/underwriting-defaults"],
    // While a newly-approved host's seeds are still in flight, poll so the
    // defaults appear the moment they're written instead of an empty form.
    refetchInterval: (query) => {
      const data = query.state.data as Record<string, unknown> | undefined;
      const hasRow = data?.id != null;
      return seedingPending && !hasRow ? 2500 : false;
    },
  });

  const hasDefaultsRow = (defaultsQuery.data as Record<string, unknown> | undefined)?.id != null;
  const showSetupPending = seedingPending && !defaultsQuery.isLoading && !hasDefaultsRow;

  useEffect(() => {
    if (defaultsQuery.data) {
      setLocalFields(defaultsQuery.data as Record<string, unknown>);
    }
  }, [defaultsQuery.data]);

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      await apiRequest("PUT", "/api/app/underwriting-defaults", data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/underwriting-defaults"] });
    },
    onError: (error: Error) => {
      if (error.message.includes("401")) {
        toast({ title: "Session expired", description: "Please log in again.", variant: "destructive" });
        navigate("/app/login");
      } else {
        toast({ title: "Failed to save default", variant: "destructive" });
      }
    },
  });

  const f = useCallback((field: string): string => {
    const v = localFields[field];
    if (v === undefined || v === null) return "";
    return String(v);
  }, [localFields]);

  const setField = useCallback((field: string, value: string | number | boolean) => {
    setLocalFields((prev) => ({ ...prev, [field]: value }));
  }, []);

  const saveField = useCallback((field: string) => {
    const value = localFields[field];
    updateMutation.mutate({ [field]: value });
  }, [localFields, updateMutation]);

  if (defaultsQuery.isLoading) {
    return (
      <AppLayout>
        <div className="p-6 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-[400px] w-full" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 max-w-4xl">
        <ViewOnlyBanner area="underwriting" />
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-2xl font-bold text-gray-900" data-testid="text-defaults-heading" data-tour="underwriting-defaults-heading">Underwriting</h1>
          <div className="flex items-center gap-2">
            <ExplainerVideoButton pageKey="underwriting-defaults" />
            <GuideButton pageKey="underwriting-defaults" />
          </div>
        </div>
        <GuideNudgeBanner pageKey="underwriting-defaults" />

        <div className="border-b border-gray-200 mb-6">
          <div className="flex gap-0">
            {([
              { key: "analyses", label: "Analyses", href: "/app/underwriting" },
              { key: "defaults", label: "Defaults", href: "/app/underwriting/defaults" },
            ]).map((tab) => {
              const isActive = tab.key === "defaults";
              return (
                <a
                  key={tab.key}
                  href={tab.href}
                  onClick={(e) => { e.preventDefault(); navigate(tab.href); }}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    isActive
                      ? "border-brand text-brand"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                  data-testid={`tab-underwriting-${tab.key}`}
                >
                  {tab.label}
                </a>
              );
            })}
          </div>
        </div>

        <p className="text-sm text-gray-500 mb-6" data-testid="text-defaults-description">
          Set default values for new underwriting analyses. These can be overridden in any individual deal.
        </p>

        {showSetupPending ? (
          <AccountSetupPending label="underwriting defaults" />
        ) : (
        <>
        <SetupCompletenessBanner
          missing={getMissingUnderwritingFields(defaultsQuery.data as Record<string, unknown> | undefined)}
          total={UNDERWRITING_REQUIRED_FIELDS.length}
          readyLabel="All defaults set — ready for scanning"
          testIdPrefix="underwriting-setup"
        />

        <fieldset disabled={readOnly} style={{ display: "contents" }}>
        <div className="space-y-6">
          <div className="border rounded-lg p-5" data-tour="underwriting-defaults-financing">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Financing & Cash</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Downpayment %" value={f("downpaymentPercent")} onChange={(v) => setField("downpaymentPercent", v)} onBlur={() => saveField("downpaymentPercent")} suffix="%" testId="input-def-downpayment" />
              <Field label="Closing Cost %" value={f("closingCostsPercent")} onChange={(v) => setField("closingCostsPercent", v)} onBlur={() => saveField("closingCostsPercent")} suffix="%" testId="input-def-closing" />
              <Field label="Furnishing Cost Per Room" value={f("furnishingCostPerRoom")} onChange={(v) => setField("furnishingCostPerRoom", v)} onBlur={() => saveField("furnishingCostPerRoom")} suffix="$" testId="input-def-furnishing" />
              <Field label="Months Until Occupied" value={f("monthsUntilOccupied")} onChange={(v) => setField("monthsUntilOccupied", v)} onBlur={() => saveField("monthsUntilOccupied")} type="number" testId="input-def-months-occupied" />
              <Field label="Annual Interest Rate" value={f("annualInterestRate")} onChange={(v) => setField("annualInterestRate", v)} onBlur={() => saveField("annualInterestRate")} suffix="%" testId="input-def-interest-rate" />
              <Field label="Vacancy %" value={f("vacancyPercent")} onChange={(v) => setField("vacancyPercent", v)} onBlur={() => saveField("vacancyPercent")} suffix="%" testId="input-def-vacancy" />
              <Field label="Option Fee" value={f("optionFee")} onChange={(v) => setField("optionFee", v)} onBlur={() => saveField("optionFee")} suffix="$" testId="input-def-option-fee" />
              <Field label="Closing Costs" value={f("closingCosts")} onChange={(v) => setField("closingCosts", v)} onBlur={() => saveField("closingCosts")} suffix="$" testId="input-def-closing-costs" />
            </div>
          </div>

          <div className="border rounded-lg p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Property Assumptions</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Land Value % of Purchase Price" value={f("landValuePercent")} onChange={(v) => setField("landValuePercent", v)} onBlur={() => saveField("landValuePercent")} suffix="%" testId="input-def-land-value" />
              <Field label="Annual Appreciation Avg %" value={f("annualAppreciationAvg")} onChange={(v) => setField("annualAppreciationAvg", v)} onBlur={() => saveField("annualAppreciationAvg")} suffix="%" testId="input-def-appreciation" />
              <Field label="Annual Insurance % of PP" value={f("annualInsurancePercent")} onChange={(v) => setField("annualInsurancePercent", v)} onBlur={() => saveField("annualInsurancePercent")} suffix="%" testId="input-def-insurance" />
              <Field label="Annual Property Tax % of PP" value={f("annualPropertyTaxPercent")} onChange={(v) => setField("annualPropertyTaxPercent", v)} onBlur={() => saveField("annualPropertyTaxPercent")} suffix="%" testId="input-def-prop-tax" />
            </div>
          </div>

          <div className="border rounded-lg p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Management Fees</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="PM Fee" value={f("pmFee")} onChange={(v) => setField("pmFee", v)} onBlur={() => saveField("pmFee")} testId="input-def-pm-fee" />
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">PM Fee Type</label>
                <Select
                  value={f("pmFeeType") || "percent"}
                  onValueChange={(v) => { setField("pmFeeType", v); updateMutation.mutate({ pmFeeType: v }); }}
                >
                  <SelectTrigger className="text-sm" data-testid="select-def-pm-fee-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Percentage (%)</SelectItem>
                    <SelectItem value="fixed">Fixed ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="border rounded-lg p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Room Defaults</h3>
            <div className="flex items-center gap-3 mb-2">
              <Switch
                checked={localFields.defaultFurnished === true || localFields.defaultFurnished === undefined}
                onCheckedChange={(checked) => { setField("defaultFurnished", checked); updateMutation.mutate({ defaultFurnished: checked }); }}
                data-testid="switch-def-furnished"
              />
              <label className="text-sm text-gray-700">Default Furnished</label>
            </div>
          </div>

          <div className="border rounded-lg p-5" data-tour="underwriting-defaults-expenses">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Monthly Expenses</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Sewer & Water" value={f("sewerWater")} onChange={(v) => setField("sewerWater", v)} onBlur={() => saveField("sewerWater")} suffix="$" testId="input-def-sewer" />
              <Field label="Gas" value={f("gas")} onChange={(v) => setField("gas", v)} onBlur={() => saveField("gas")} suffix="$" testId="input-def-gas" />
              <Field label="Electricity (base)" value={f("electricity")} onChange={(v) => setField("electricity", v)} onBlur={() => saveField("electricity")} suffix="$" testId="input-def-electricity" />
              <Field label="Electricity Per Room" value={f("electricityPerRoom")} onChange={(v) => setField("electricityPerRoom", v)} onBlur={() => saveField("electricityPerRoom")} suffix="$/room" testId="input-def-electricity-per-room" />
              <Field label="Internet" value={f("internet")} onChange={(v) => setField("internet", v)} onBlur={() => saveField("internet")} suffix="$" testId="input-def-internet" />
              <Field label="Lawn Care" value={f("lawnCare")} onChange={(v) => setField("lawnCare", v)} onBlur={() => saveField("lawnCare")} suffix="$" testId="input-def-lawn" />
              <Field label="Cleaning" value={f("cleaning")} onChange={(v) => setField("cleaning", v)} onBlur={() => saveField("cleaning")} suffix="$" testId="input-def-cleaning" />
              <Field label="Capital Improvements Budget" value={f("capitalImprovementsBudget")} onChange={(v) => setField("capitalImprovementsBudget", v)} onBlur={() => saveField("capitalImprovementsBudget")} suffix="$" testId="input-def-capex-budget" />
              <Field label="Misc Repairs" value={f("miscRepairs")} onChange={(v) => setField("miscRepairs", v)} onBlur={() => saveField("miscRepairs")} suffix="$" testId="input-def-misc" />
              <Field label="HOA" value={f("hoa")} onChange={(v) => setField("hoa", v)} onBlur={() => saveField("hoa")} suffix="$" testId="input-def-hoa" />
            </div>
          </div>

          <div className="border rounded-lg p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-4">Remodel / CapEx Defaults</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Field label="Room Addition Cost" value={f("roomAdditionsCost")} onChange={(v) => setField("roomAdditionsCost", v)} onBlur={() => saveField("roomAdditionsCost")} suffix="$" testId="input-def-room-add-cost" />
              <Field label="Bath Addition Cost" value={f("bathAdditionsCost")} onChange={(v) => setField("bathAdditionsCost", v)} onBlur={() => saveField("bathAdditionsCost")} suffix="$" testId="input-def-bath-add-cost" />
              <Field label="Kitchen Amount" value={f("kitchenAmount")} onChange={(v) => setField("kitchenAmount", v)} onBlur={() => saveField("kitchenAmount")} suffix="$" testId="input-def-kitchen" />
              <Field label="HVAC Amount" value={f("hvacAmount")} onChange={(v) => setField("hvacAmount", v)} onBlur={() => saveField("hvacAmount")} suffix="$" testId="input-def-hvac" />
              <Field label="Roof Amount" value={f("roofAmount")} onChange={(v) => setField("roofAmount", v)} onBlur={() => saveField("roofAmount")} suffix="$" testId="input-def-roof" />
              <Field label="Plumbing Amount" value={f("plumbingAmount")} onChange={(v) => setField("plumbingAmount", v)} onBlur={() => saveField("plumbingAmount")} suffix="$" testId="input-def-plumbing" />
              <Field label="Foundation Repair Amount" value={f("foundationRepairAmount")} onChange={(v) => setField("foundationRepairAmount", v)} onBlur={() => saveField("foundationRepairAmount")} suffix="$" testId="input-def-foundation" />
              <Field label="Other Amount" value={f("otherAmount")} onChange={(v) => setField("otherAmount", v)} onBlur={() => saveField("otherAmount")} suffix="$" testId="input-def-other" />
              <Field label="Additional Rehab % of PP" value={f("additionalRehabAmount")} onChange={(v) => setField("additionalRehabAmount", v)} onBlur={() => saveField("additionalRehabAmount")} suffix="%" testId="input-def-rehab" />
              <Field label="Buffer %" value={f("bufferPercent")} onChange={(v) => setField("bufferPercent", v)} onBlur={() => saveField("bufferPercent")} suffix="%" testId="input-def-buffer" />
            </div>
          </div>
        </div>
        </fieldset>
        </>
        )}
      </div>
    </AppLayout>
  );
}
